/* Volume discount (ADR 0038) — the client-side mirror of database/volume_discount.py.

   A percentage off a Payer's Subscription charges, set by how many Accounts that Payer pays a
   Subscription for. The server decides every charge; these helpers only preview it (the tier a
   checkout lands on) and read it back off a ledger row for the receipt line. */

import type { VolumeDiscountSummary, VolumeLadderStep } from '../../api/client';

export interface TierPreview {
  bps: number;
  /** Paid Accounts the charge counts: already paid + this checkout. */
  count: number;
  /** How many more Accounts reach the next ladder step, or null (40+, override, kill switch). */
  next: { accounts_needed: number; bps: number } | null;
}

function ladderBps(ladder: VolumeLadderStep[], count: number) {
  return ladder.reduce((bps, s) => (count >= s.min_accounts ? s.bps : bps), 0);
}

function nextStep(ladder: VolumeLadderStep[], count: number) {
  const s = ladder.find((x) => count < x.min_accounts);
  return s ? { accounts_needed: s.min_accounts - count, bps: s.bps } : null;
}

/** The tier a checkout of `cartCount` new Accounts lands on. */
export function cartVolume(vd: VolumeDiscountSummary, cartCount: number): TierPreview {
  const count = vd.paid_count + cartCount;
  if (vd.source === 'off') return { bps: 0, count, next: null };
  if (vd.source === 'override') return { bps: vd.bps, count, next: null };
  return { bps: ladderBps(vd.ladder, count), count, next: nextStep(vd.ladder, count) };
}

/** The discount `bps` takes off `amountCents`, rounded half up — the ledger's rule. */
export function volumeOffCents(amountCents: number, bps: number) {
  return Math.floor((amountCents * bps + 5_000) / 10_000);
}

/** The Volume discount a ledger row was charged at, or null when none applied. */
export function ledgerVolume(m: Record<string, unknown>): { bps: number; count: number; special: boolean } | null {
  const bps = Number(m.volume_discount_bps) || 0;
  if (bps <= 0 || m.discount_kind === 'promo') return null;
  return { bps, count: Number(m.volume_paid_count) || 0, special: m.volume_source === 'override' };
}

export const pct = (bps: number) => (bps / 100).toLocaleString('en-US', { maximumFractionDigits: 2 }) + '%';

/** From here on the ladder stops: the Payer talks to us about a special rate. */
export const NEGOTIATED_FROM = 50;

export interface LadderRow {
  /** "1–9", "10–19", …, "50+". */
  label: string;
  min: number;
  /** null on the negotiated 50+ row. */
  bps: number | null;
  /** The step the Payer is on (never with an override or the kill switch). */
  current: boolean;
  /** Paid Accounts still missing to reach this step; 0 once reached. */
  needed: number;
}

/** The whole ladder as the Payer sees it: 1–9 at 0%, each step, then the negotiated 50+. */
export function ladderRows(vd: VolumeDiscountSummary): LadderRow[] {
  const steps = [{ min_accounts: 1, bps: 0 }, ...vd.ladder];
  const onLadder = vd.source === 'ladder';
  const here = onLadder ? steps.reduce((c, s, i) => (vd.paid_count >= s.min_accounts ? i : c), 0) : -1;
  const negotiated = onLadder && vd.paid_count >= NEGOTIATED_FROM;
  const rows: LadderRow[] = steps.map((s, i) => {
    const end = (steps[i + 1]?.min_accounts ?? NEGOTIATED_FROM) - 1;
    return {
      label: end > s.min_accounts ? `${s.min_accounts}–${end}` : String(s.min_accounts),
      min: s.min_accounts,
      bps: s.bps,
      current: i === here && !negotiated,
      needed: Math.max(0, s.min_accounts - vd.paid_count),
    };
  });
  rows.push({ label: `${NEGOTIATED_FROM}+`, min: NEGOTIATED_FROM, bps: null, current: negotiated, needed: Math.max(0, NEGOTIATED_FROM - vd.paid_count) });
  return rows;
}
